import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DatasetDialog, { Field, inputClass, primaryClass, neutralClass } from 'features/datasets/components/DatasetDialog';
import { previewSource, createSource, updateSource, createDataset, runSource } from 'features/datasets/api/datasetsApi';
import { conditionsToFilter } from 'features/datasets/components/EvalConditions';
import EvalFilterSection from 'features/agent-analytics/components/EvalFilterSection';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import {
  QUERY_PANEL_CLASS, QUERY_SECTION_HEADER_CLASS, QUERY_SECTION_LABEL_CLASS, QUERY_SECTION_BODY_CLASS, QUERY_SECTION_DIVIDER_CLASS,
} from 'shared/components/filters/queryBuilderChrome';
import SpansQueryBuilder, { buildSpanModularFilters } from 'features/agent-analytics/components/SpansQueryBuilder';
import AgentRunsQueryBuilder, { buildModularFilters } from 'features/agent-analytics/components/AgentRunsQueryBuilder';
import AgentMultiSelect from 'features/agent-analytics/components/AgentMultiSelect';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import BrandMultiSelect from 'features/playground/components/BrandMultiSelect';
import { listLineages, getLineage } from 'features/evals3/api/evals3Api';
import BrandSelect from 'features/playground/components/BrandSelect';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';
import { presetToISORange, DEFAULT_LIST_PRESET } from 'shared/utils/timePresets';
import { KIND_LABEL, kindOf, filterSummary, stateFromSource, isRelativePreset } from 'features/datasets/utils/sources';

/**
 * A source: a saved filter that fills a dataset (docs/DATASETS.md).
 *
 *   Rows from   Spans | Runs | Conversations | Verdicts
 *   Where       the Traces filter itself (its sections, Evals among them) and a time window
 *   Live        off: copy what matches now · on: keep adding new matches
 *
 * Three ways in:
 *   add to a dataset        { datasetId }                     → POST sources
 *   a new dataset's first   { newDataset: true, initial }     → create the dataset, then its source
 *     source (the /new draft)
 *   edit a source           { datasetId, source }             → PATCH, opened on the filter builder
 */

const KINDS = ['spans', 'runs', 'conversations', 'verdicts'];
const OUTCOMES = [['failed', 'Failed'], ['passed', 'Passed'], ['marked_wrong', 'Marked wrong'], ['marked_right', 'Marked right']];
/** With one step as the row: verdicts where someone corrected (or confirmed) THAT step. */
const STEP_OUTCOMES = [['step_corrected', 'Step corrected']];

/** The steps of an eval that answer something: the rows a step-level dataset can be made of. */
export function answeringSteps(graph) {
  return Object.entries((graph && graph.nodes) || {})
    .filter(([, n]) => n && n.type !== 'verdict' && n.output && Object.keys(n.output).length)
    .map(([id, n]) => ({ id, name: n.name || id }));
}
const smallLabel = QUERY_SECTION_LABEL_CLASS;

/** One section of the filter panel, in the Traces filter's chrome. */
function Section({ label, divider = false, children }) {
  return (
    <>
      {divider ? <div className={QUERY_SECTION_DIVIDER_CLASS} /> : null}
      <div className={QUERY_SECTION_HEADER_CLASS}><h3 className={QUERY_SECTION_LABEL_CLASS}>{label}</h3></div>
      <div className={QUERY_SECTION_BODY_CLASS}>{children}</div>
    </>
  );
}

/** The dialog's state → the server's { kind, filter, options }. */
export function specOf(s) {
  // A relative period (Today .. 3M) is kept as that preset and measured again at every run.
  // Only Custom keeps absolute dates.
  const p = s.time?.preset;
  const time = isRelativePreset(p) ? { preset: p }
    : s.time?.from || s.time?.to ? { preset: 'custom', from: s.time.from || undefined, to: s.time.to || undefined }
      : { preset: '7d' };
  if (s.kind === 'spans') {
    const filter = { modularFilters: buildSpanModularFilters({ selectedTraces: s.selectedTraces, traceScopedFilters: s.traceScopedFilters, globalFilters: s.globalFilters }), time };
    const conds = conditionsToFilter(s.conditions);
    if (conds.length) filter.eval_conditions = conds;
    if (s.anyKind) filter.span_kind = 'any';
    return { kind: 'spans', filter, options: {} };
  }
  if (s.kind === 'runs') {
    const filter = { modularFilters: buildModularFilters({ selectedAgents: s.selectedAgents, agentScopedFilters: s.agentScopedFilters, globalFilters: s.globalFilters }), time };
    const conds = conditionsToFilter(s.conditions);
    if (conds.length) filter.eval_conditions = conds;
    return { kind: 'runs', filter, options: s.includeTrace ? { include_trace: true } : {} };
  }
  if (s.kind === 'conversations') {
    const agents = (s.selectedAgents || []).map((a) => (typeof a === 'string' ? a : a?.name || a?.agent_name)).filter(Boolean);
    const filter = { modularFilters: agents.length ? [{ field: 'agent_name', operator: 'is', value: agents, category: 'agents' }] : [], time };
    // A conversation qualifies when one of its verdicts (on the thread or a run in it) does.
    const conds = conditionsToFilter(s.conditions);
    if (conds.length) filter.eval_conditions = conds;
    return { kind: 'conversations', filter, options: {} };
  }
  // Each row one verdict, or one STEP of one eval (Output = what the step answered,
  // Expected = the person's correction of that step).
  const ids = s.lineageIds || [];
  const nodeId = ids.length === 1 && s.nodeId ? s.nodeId : null;
  const outcomes = (s.outcomes || []).filter((o) => nodeId || o !== 'step_corrected');
  return { kind: 'verdicts', filter: { lineage_ids: ids, outcomes, time }, options: { rows: 'verdicts', ...(nodeId ? { node_id: nodeId } : {}) } };
}

function initialTime(initial) {
  // A saved relative period shows its preset (the dates it spans now only fill the picker).
  if (isRelativePreset(initial?.time?.preset)) {
    const range = presetToISORange(initial.time.preset);
    return { preset: initial.time.preset, from: range?.from || '', to: range?.to || '' };
  }
  if (initial?.time?.from || initial?.time?.to) return { preset: 'custom', from: initial.time.from || '', to: initial.time.to || '' };
  const seeded = presetToISORange(DEFAULT_LIST_PRESET);
  return { preset: DEFAULT_LIST_PRESET, from: seeded?.from || '', to: seeded?.to || '' };
}

/**
 * `updateSources`: Update mode. Fetch again from one of the dataset's sources
 * over the period picked, with its filters ("Use the same filters") or with
 * changed ones (added as a one-time source). Rows already held are skipped.
 */
export default function SourceDialog({ datasetId = null, datasetName = '', source = null, initial = null, newDataset = false, updateSources = null, onClose, onSaved }) {
  const { team } = useParams();
  const navigate = useNavigate();
  const teamId = getSelectedTeamNumericId();
  const updating = Array.isArray(updateSources) && updateSources.length > 0;
  const editing = Boolean(source) && !updating;
  const [pickedId, setPickedId] = useState(updating ? updateSources[0].id : null);
  const picked = updating ? (updateSources.find((x) => x.id === pickedId) || updateSources[0]) : null;
  const [sameFilters, setSameFilters] = useState(true);
  const [name, setName] = useState(initial?.name || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [lineages, setLineages] = useState([]);

  const [s, setS] = useState(() => {
    if (updating) return { ...stateFromSource(updateSources[0]), live: false, time: initialTime(null) };
    if (source) return { ...stateFromSource(source), live: Boolean(source.live), time: initialTime({ time: source.filter?.time }) };
    return {
      kind: initial?.kind || 'spans',
      live: Boolean(initial?.live),
      time: initialTime(initial),
      selectedTraces: initial?.selectedTraces || [], traceScopedFilters: initial?.traceScopedFilters || [], globalFilters: initial?.globalFilters || [],
      selectedAgents: initial?.selectedAgents || [], agentScopedFilters: initial?.agentScopedFilters || [],
      conditions: Array.isArray(initial?.evalConditions) ? initial.evalConditions : [], anyKind: false,
      lineageIds: initial?.lineageIds || [], outcomes: initial?.outcomes || ['failed'],
      nodeId: initial?.nodeId || '', includeTrace: Boolean(initial?.includeTrace),
    };
  });
  // Another source picked for the Update: start from ITS filters, keep the period.
  useEffect(() => {
    if (!updating || !picked) return;
    setS((cur) => ({ ...cur, ...stateFromSource(picked) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedId]);
  const set = (patch) => setS((cur) => ({ ...cur, ...(typeof patch === 'function' ? patch(cur) : patch) }));
  // The query builders call their setters React-style, with a value OR an
  // updater ((prev) => [...prev, chip]); resolve both against this field.
  const setList = (key) => (v) => setS((cur) => ({ ...cur, [key]: typeof v === 'function' ? v(Array.isArray(cur[key]) ? cur[key] : []) : v }));

  useEffect(() => {
    if (!teamId) return;
    Promise.resolve(listLineages(teamId)).then((l) => setLineages(l || [])).catch(() => setLineages([]));
  }, [teamId]);

  // Verdicts of ONE eval can be split by step: load that eval's steps.
  const oneEval = s.kind === 'verdicts' && (s.lineageIds || []).length === 1 ? s.lineageIds[0] : null;
  const [steps, setSteps] = useState([]);
  useEffect(() => {
    if (!oneEval || !teamId) { setSteps([]); return undefined; }
    let cancelled = false;
    Promise.resolve(getLineage(teamId, oneEval)).then((d) => { if (!cancelled) setSteps(answeringSteps(d?.current?.graph)); }).catch(() => { if (!cancelled) setSteps([]); });
    return () => { cancelled = true; };
  }, [oneEval, teamId]);
  const lineageNames = useMemo(() => Object.fromEntries((lineages || []).map((l) => [l.id, l.name])), [lineages]);

  // The spec being saved: the builder's filter, or the stored one when Update keeps it.
  const spec = useMemo(() => {
    if (updating) {
      const period = specOf(s).filter.time;
      if (sameFilters) return { kind: kindOf(picked), filter: { ...(picked.filter || {}), time: period }, options: picked.options || {} };
      const built = specOf(s);
      return { ...built, options: { ...(picked.options || {}), ...built.options } };
    }
    return specOf(s);
  }, [updating, sameFilters, picked, s]);

  // Preview: how many units match, debounced as the filter changes.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const seq = useRef(0);
  useEffect(() => {
    const mine = ++seq.current;
    setPreviewing(true);
    const t = setTimeout(() => {
      previewSource(spec)
        .then((p) => { if (mine === seq.current) setPreview(p); })
        .catch((e) => { if (mine === seq.current) { setPreview(null); setError(e?.response?.data?.message || e?.response?.data?.error || e.message); } })
        .finally(() => { if (mine === seq.current) setPreviewing(false); });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(spec)]);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (updating) {
        const body = sameFilters ? { time: spec.filter.time } : { time: spec.filter.time, filter: spec.filter };
        const out = await runSource(datasetId, picked.id, body);
        onSaved?.(out.source || picked);
        onClose();
        return;
      }
      if (editing) {
        const out = await updateSource(datasetId, source.id, { live: s.live, kind: spec.kind, filter: spec.filter, options: spec.options });
        onSaved?.(out.source);
        onClose();
        return;
      }
      let id = datasetId;
      if (newDataset) {
        const trimmed = name.trim();
        if (!trimmed) { setError('Name the dataset.'); setBusy(false); return; }
        id = (await createDataset(trimmed)).id;
      }
      const out = await createSource(id, { ...spec, live: s.live });
      onSaved?.(out.source);
      onClose();
      if (newDataset) navigate(`/${encodeURIComponent(team)}/datasets/${id}`);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  // "Step corrected" only means something when one step is the row.
  const outcomeOptions = [...OUTCOMES, ...(s.nodeId && oneEval ? STEP_OUTCOMES : [])].map(([value, label]) => ({ value, label }));

  const unitWord = s.kind === 'verdicts' ? 'verdicts' : s.kind;
  const matchedAmount = preview && preview.capped
    ? `${Number(preview.matched).toLocaleString()}+`
    : preview ? preview.matched.toLocaleString() : '';
  const matchedText = previewing && !preview ? 'Counting…' : preview
    ? (preview.capped || preview.matched !== 1
      ? `${matchedAmount} ${unitWord} match`
      : `1 ${unitWord.replace(/s$/, '')} matches`)
    : '';
  const title = updating ? `Update · ${datasetName || 'dataset'}` : editing ? `Source · ${KIND_LABEL[kindOf(source)]}` : newDataset ? `Add source · ${name.trim() || 'new dataset'}` : `Add source · ${datasetName || 'dataset'}`;
  const footer = (
    <>
      <span className="mr-auto text-[12px] text-[color:var(--text-muted)]" aria-live="polite">
        {matchedText}
      </span>
      <button type="button" onClick={onClose} className={neutralClass}>Cancel</button>
      <button type="button" onClick={save} disabled={busy || (newDataset && !name.trim())} className={primaryClass}>
        {busy ? 'Saving…' : updating ? 'Update' : editing ? 'Save' : newDataset ? 'Create dataset' : 'Add source'}
      </button>
    </>
  );

  const timeFilter = (
    <EventsTimeFilter selectedPreset={s.time.preset || DEFAULT_LIST_PRESET}
      onTimeFilterChange={(next) => set({ time: { preset: next?.preset || (next?.type === 'custom' ? 'custom' : ''), from: next?.from || '', to: next?.to || '' } })} />
  );

  return (
    <DatasetDialog title={title} onClose={onClose} width="max-w-[760px]" footer={footer} bodyClassName="min-h-[360px]">
      {newDataset ? (
        <Field label="Name" htmlFor="src-name">
          <input id="src-name" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Billing failures" autoFocus />{/* eslint-disable-line jsx-a11y/no-autofocus */}
        </Field>
      ) : null}

      {updating ? (
        <>
          {updateSources.length > 1 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className={smallLabel}>Source</span>
              <BrandSelect ariaLabel="Source" value={picked.id} minWidth="320px"
                options={updateSources.map((x) => ({ value: x.id, label: `${KIND_LABEL[kindOf(x)]} · ${filterSummary(x, lineageNames, { withTime: false }) || 'no filters'}` }))}
                onChange={setPickedId} />
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <span className={smallLabel}>Period</span>
            {timeFilter}
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-[12px] text-[color:var(--text-primary)]">
            <input type="checkbox" checked={sameFilters} onChange={(e) => setSameFilters(e.target.checked)} />
            Use the same filters
          </label>
          {sameFilters ? (
            <div className="rounded-lg border border-[color:var(--border-default)] px-3 py-2 text-[12px] text-[color:var(--text-secondary)]">
              {KIND_LABEL[kindOf(picked)]} · {filterSummary(picked, lineageNames, { withTime: false }) || 'no filters'}
            </div>
          ) : null}
        </>
      ) : null}

      {updating && sameFilters ? null : (
        <div className="flex flex-wrap items-center gap-2">
          {updating || editing ? null : (
            <SegmentedTabs ariaLabel="Rows from" value={s.kind}
              options={KINDS.map((k) => ({ value: k, label: KIND_LABEL[k] }))}
              onChange={(k) => { setError(null); set({ kind: k, conditions: [] }); }} />
          )}
          {editing ? <span className="text-[12px] font-medium text-[color:var(--text-primary)]">{KIND_LABEL[s.kind]}</span> : null}
          {updating ? null : timeFilter}
          {s.kind === 'spans' ? (
            <label className="ml-auto flex items-center gap-1.5 text-[12px] text-[color:var(--text-secondary)]">
              <input type="checkbox" checked={s.anyKind} onChange={(e) => set({ anyKind: e.target.checked })} /> Include non-LLM spans
            </label>
          ) : null}
        </div>
      )}

      {updating && sameFilters ? null : (
        <>
          {s.kind === 'spans' ? (
            <SpansQueryBuilder teamId={teamId} shown
              selectedTraces={s.selectedTraces} onSelectedTracesChange={setList('selectedTraces')}
              traceScopedFilters={s.traceScopedFilters} onTraceScopedFiltersChange={setList('traceScopedFilters')}
              globalFilters={s.globalFilters} onGlobalFiltersChange={setList('globalFilters')}
              evalLines={s.conditions} onEvalLinesChange={(v) => set({ conditions: v })} />
          ) : null}
          {s.kind === 'runs' ? (
            <AgentRunsQueryBuilder teamId={teamId} shown
              selectedAgents={s.selectedAgents} onSelectedAgentsChange={setList('selectedAgents')}
              agentScopedFilters={s.agentScopedFilters} onAgentScopedFiltersChange={setList('agentScopedFilters')}
              globalFilters={s.globalFilters} onGlobalFiltersChange={setList('globalFilters')}
              evalLines={s.conditions} onEvalLinesChange={(v) => set({ conditions: v })} />
          ) : null}
          {s.kind === 'conversations' ? (
            <div className={QUERY_PANEL_CLASS}>
              <Section label="Agents">
                <AgentMultiSelect teamId={teamId} value={s.selectedAgents} onChange={(v) => set({ selectedAgents: v })} />
              </Section>
              <div className={QUERY_SECTION_DIVIDER_CLASS} />
              <EvalFilterSection teamId={teamId} label="Evals" value={s.conditions} onChange={(v) => set({ conditions: v })} />
            </div>
          ) : null}
          {s.kind === 'verdicts' ? (
            <div className={QUERY_PANEL_CLASS}>
              <Section label="Evals">
                <BrandMultiSelect value={s.lineageIds} options={lineages.map((l) => ({ value: l.id, label: l.name }))} onChange={(ids) => set({ lineageIds: ids, nodeId: '' })} placeholder="Every eval" ariaLabel="Evals" minWidth="100%" />
              </Section>
              {oneEval ? (
                <Section label="Each row is" divider>
                  <BrandSelect ariaLabel="Each row is" value={s.nodeId || ''} minWidth="220px"
                    options={[{ value: '', label: 'The whole verdict' }, ...steps.map((st) => ({ value: st.id, label: `Step: ${st.name}` }))]}
                    onChange={(v) => set((cur) => ({ nodeId: v, outcomes: v ? cur.outcomes : cur.outcomes.filter((o) => o !== 'step_corrected') }))} />
                </Section>
              ) : null}
              <Section label="Verdicts that" divider>
                <BrandMultiSelect ariaLabel="Verdicts that" value={s.outcomes} options={outcomeOptions} onChange={(ids) => set({ outcomes: ids })} placeholder="Any verdict" minWidth="100%" />
              </Section>
            </div>
          ) : null}
        </>
      )}

      {updating ? (
        <p className="text-[12px] text-[color:var(--text-muted)]">Rows already in the dataset are skipped; a conversation with new runs is brought up to date.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-[12px] text-[color:var(--text-primary)]">
            <input type="checkbox" checked={s.live} onChange={(e) => set({ live: e.target.checked })} />
            Keep adding new matches
          </label>
          {s.kind === 'runs' ? (
            <label className="flex cursor-pointer items-center gap-2 text-[12px] text-[color:var(--text-primary)]">
              <input type="checkbox" checked={Boolean(s.includeTrace)} onChange={(e) => set({ includeTrace: e.target.checked })} />
              Keep every span of each run
            </label>
          ) : null}
        </div>
      )}

      {error ? <p className="text-[12px] text-danger">{error}</p> : null}
    </DatasetDialog>
  );
}
